import {useAppDataStore} from "@/mobx/appDataStore.jsx";
import {useRef, useState} from "react";
import {observer} from "mobx-react";
import moment from "moment";
import * as XLSX from "xlsx/xlsx.mjs";

const EXCEL_COLUMNS = [
  {key: 'Record Date', label: 'Record Date'},
  {key: 'Pais', label: 'País'},
  {key: 'Provider', label: 'Provider'},
  {key: 'Channel', label: 'Channel'},
  {key: 'Subject ID', label: 'Subject ID'},
  {key: 'Entitlement ID', label: 'Entitlement ID'},
  {key: 'SKU', label: 'SKU'},
  {key: 'SKU Description', label: 'SKU Description'},
  {key: 'Status', label: 'Status'},
  {key: 'Cancellation', label: 'Cancellation'},
  {key: 'Offer ID', label: 'Offer ID'},
  {key: 'Retail Price', label: 'Retail Price'},
]

const SALE_COLUMNS = [
  {key: 'sale_createdAt', label: 'Fecha Venta'},
  {key: 'sale_product', label: 'Producto'},
  {key: 'sale_amount', label: 'Monto'},
  {key: 'sale_result', label: 'Resultado'},
  {key: 'sale_customerName', label: 'Cliente'},
  {key: 'sale_email', label: 'Email'},
  {key: '_date_match', label: 'Fecha Concuerda'},
]

function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      const base64 = e.target.result.split(',')[1]
      resolve(base64)
    }
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

export default observer(function AdminReconciliations() {
  const appDataStore = useAppDataStore()
  const fileInputRef = useRef(null)
  const [file, setFile] = useState(null)
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState(null)
  const [filter, setFilter] = useState('all')
  const [error, setError] = useState(null)

  const userScopes = appDataStore.user?.scopes || []
  const hasMasterScope = Array.isArray(userScopes) && userScopes.includes('master')

  const handleFileChange = (e) => {
    const selected = e.target.files[0]
    if (selected) {
      setFile(selected)
      setData(null)
      setError(null)
    }
  }

  const handleProcess = async () => {
    if (!file) return
    try {
      setLoading(true)
      setError(null)
      const fileBase64 = await readFileAsBase64(file)
      const res = await appDataStore.post('/reconciliations/process', {fileBase64})
      setData(res.data || [])
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const computeDateMatch = (row) => {
    if (!row.sale_createdAt || !row['Record Date']) return ''
    const excelDate = moment(row['Record Date'], ['YYYY-MM-DD', 'DD/MM/YYYY', 'MM/DD/YYYY', moment.ISO_8601])
    const saleDate = moment(row.sale_createdAt)
    return excelDate.isValid() && saleDate.isValid() && excelDate.format('YYYY-MM-DD') === saleDate.format('YYYY-MM-DD') ? 'Si' : 'No'
  }

  const handleExport = () => {
    const exportRows = filteredData.map(row => ({
      'Record Date': String(row['Record Date'] ?? ''),
      'Pais': String(row.Pais ?? ''),
      'Provider': String(row.Provider ?? ''),
      'Channel': String(row.Channel ?? ''),
      'Subject ID': String(row['Subject ID'] ?? ''),
      'Entitlement ID': String(row['Entitlement ID'] ?? ''),
      'SKU': String(row.SKU ?? ''),
      'SKU Description': String(row['SKU Description'] ?? ''),
      'Status': String(row.Status ?? ''),
      'Cancellation': String(row.Cancellation ?? ''),
      'Offer ID': String(row['Offer ID'] ?? ''),
      'Retail Price': String(row['Retail Price'] ?? ''),
      'Fecha Venta': row.sale_createdAt ? moment(row.sale_createdAt).format('YYYY-MM-DD HH:mm') : '',
      'Producto': String(row.sale_product ?? ''),
      'Monto': String(row.sale_amount ?? ''),
      'Resultado': String(row.sale_result ?? ''),
      'Cliente': String(row.sale_customerName ?? ''),
      'Email': String(row.sale_email ?? ''),
      'Fecha Concuerda': computeDateMatch(row),
    }))
    const workbook = XLSX.utils.book_new()
    const worksheet = XLSX.utils.json_to_sheet(exportRows)
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Conciliacion')
    XLSX.writeFile(workbook, 'conciliacion.xlsx')
  }

  const filteredData = data ? data.filter(row => {
    if (filter === 'matched') return row.matched
    if (filter === 'unmatched') return !row.matched
    return true
  }) : []

  const matchedCount = data ? data.filter(r => r.matched).length : 0
  const unmatchedCount = data ? data.filter(r => !r.matched).length : 0

  if (!hasMasterScope) {
    return (
      <div className="p-2 max-w-7xl mx-auto text-black">
        <div className="bg-red-50 border border-red-300 rounded-lg p-6 max-w-2xl">
          <h3 className="text-lg font-bold text-red-800 mb-2">Acceso Denegado</h3>
          <p className="text-red-700">Se requiere el scope <strong>Master</strong>.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-2 mx-auto text-black">
      {/* Upload area */}
      <div className="mb-6 flex items-center gap-4">
        <div
          className="flex-1 border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-gray-400 transition-colors"
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={handleFileChange}
            className="hidden"
          />
          {file ? (
            <div className="text-gray-800">
              <p className="font-semibold">{file.name}</p>
              <p className="text-sm text-gray-500">{(file.size / 1024).toFixed(1)} KB — Click para cambiar</p>
            </div>
          ) : (
            <div className="text-gray-500">
              <p className="font-medium">Seleccionar archivo Excel</p>
              <p className="text-sm mt-1">.xlsx, .xls o .csv</p>
            </div>
          )}
        </div>

        <button
          onClick={handleProcess}
          disabled={!file || loading}
          className={`px-6 py-3 rounded-lg font-semibold text-white transition-colors ${
            !file || loading
              ? 'bg-gray-300 cursor-not-allowed'
              : 'bg-blue-600 hover:bg-blue-700'
          }`}
        >
          {loading ? 'Procesando...' : 'Procesar'}
        </button>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-300 rounded-lg p-4 text-red-700">
          {error}
        </div>
      )}

      {data && (
        <>
          {/* Summary */}
          <div className="mb-4 grid grid-cols-3 gap-4">
            <div className="bg-gray-100 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-gray-800">{data.length}</p>
              <p className="text-sm text-gray-500">Total filas</p>
            </div>
            <div className="bg-green-50 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-green-700">{matchedCount}</p>
              <p className="text-sm text-green-600">Con coincidencia</p>
            </div>
            <div className="bg-red-50 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-red-700">{unmatchedCount}</p>
              <p className="text-sm text-red-600">Sin coincidencia</p>
            </div>
          </div>

          {/* Filter + Export */}
          <div className="mb-3 flex items-center justify-between gap-2">
            <div className="flex gap-2">
            {[
              {value: 'all', label: 'Todas'},
              {value: 'matched', label: 'Con coincidencia'},
              {value: 'unmatched', label: 'Sin coincidencia'},
            ].map(opt => (
              <button
                key={opt.value}
                onClick={() => setFilter(opt.value)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  filter === opt.value
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {opt.label}
              </button>
            ))}
            </div>
            <button
              onClick={handleExport}
              className="px-4 py-1.5 rounded-lg text-sm font-semibold bg-emerald-600 hover:bg-emerald-700 text-white transition-colors"
            >
              Descargar Excel
            </button>
          </div>

          {/* Table */}
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full text-xs">
              <thead>
                <tr className="bg-gray-700 text-white">
                  <th className="px-2 py-2 text-left font-semibold" colSpan={EXCEL_COLUMNS.length}>
                    Datos Excel
                  </th>
                  <th className="px-2 py-2 text-left font-semibold border-l-2 border-yellow-400" colSpan={SALE_COLUMNS.length}>
                    Datos Venta (BD)
                  </th>
                </tr>
                <tr className="bg-gray-600 text-gray-200">
                  {EXCEL_COLUMNS.map(col => (
                    <th key={col.key} className="px-2 py-1.5 text-left whitespace-nowrap">{col.label}</th>
                  ))}
                  {SALE_COLUMNS.map((col, i) => (
                    <th key={col.key} className={`px-2 py-1.5 text-left whitespace-nowrap ${i === 0 ? 'border-l-2 border-yellow-400' : ''}`}>{col.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredData.map((row, idx) => (
                  <tr
                    key={idx}
                    className={`border-t border-gray-100 ${
                      row.matched
                        ? idx % 2 === 0 ? 'bg-green-50' : 'bg-green-100/50'
                        : idx % 2 === 0 ? 'bg-red-50' : 'bg-red-100/50'
                    }`}
                  >
                    {EXCEL_COLUMNS.map(col => (
                      <td key={col.key} className="px-2 py-1.5 whitespace-nowrap text-gray-700">
                        {String(row[col.key] ?? '')}
                      </td>
                    ))}
                    {SALE_COLUMNS.map((col, i) => (
                      <td key={col.key} className={`px-2 py-1.5 whitespace-nowrap ${i === 0 ? 'border-l-2 border-yellow-400' : ''} ${row.matched ? 'text-gray-700' : 'text-gray-300'}`}>
                        {col.key === 'sale_createdAt' && row[col.key]
                          ? moment(row[col.key]).format('YYYY-MM-DD HH:mm')
                          : col.key === 'sale_result' && row[col.key]
                            ? <span className={`px-1.5 py-0.5 rounded text-xs font-semibold ${row[col.key] === 'SUCCESS' ? 'bg-green-500 text-white' : 'bg-red-200 text-red-800'}`}>{row[col.key]}</span>
                            : col.key === '_date_match'
                              ? (() => {
                                  const result = computeDateMatch(row)
                                  if (!result) return <span className="text-gray-300">—</span>
                                  return result === 'Si'
                                    ? <span className="px-1.5 py-0.5 rounded text-xs font-semibold bg-green-500 text-white">Si</span>
                                    : <span className="px-1.5 py-0.5 rounded text-xs font-semibold bg-red-200 text-red-800">No</span>
                                })()
                              : String(row[col.key] ?? '')}
                      </td>
                    ))}
                  </tr>
                ))}
                {filteredData.length === 0 && (
                  <tr>
                    <td colSpan={EXCEL_COLUMNS.length + SALE_COLUMNS.length} className="px-4 py-8 text-center text-gray-400">
                      No hay datos
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  )
})